// src/components/ProxyGenerator.js
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';

const ProxyGenerator = () => {
  const [searchParams] = useSearchParams();
  const [locationData, setLocationData] = useState([]);
  const [prefixes, setPrefixes] = useState({ country: '', city: '', state: '' });
  
  // Search states
  const [countrySearch, setCountrySearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  
  // Selection states
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  
  // Form states
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [generatedProxy, setGeneratedProxy] = useState(null);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [copiedProxy, setCopiedProxy] = useState({ http: '', socks5: '' });

  // Fetch location data
  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const response = await fetch('https://api.elite.nexaproxy.com/proxies/countries');
        const data = await response.json();
        setLocationData(data.countries);
        setPrefixes({
          country: data.prefix,
          city: data.countries[0]?.cities?.prefix || '',
          state: data.countries[0]?.states?.prefix || ''
        });
      } catch (error) {
        console.error('Error fetching location data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocationData();
  }, []);

  // Get URL parameters
  useEffect(() => {
    const urlUsername = searchParams.get('username');
    const urlPassword = searchParams.get('password');
    if (urlUsername) setUsername(urlUsername);
    if (urlPassword) setPassword(urlPassword);
  }, [searchParams]);

  // Filtered countries
  const filteredCountries = useMemo(() => 
    locationData.filter(c => 
      c.name.toLowerCase().includes(countrySearch.toLowerCase())
    ),
    [locationData, countrySearch]
  );

  // Filtered locations
  const filteredLocations = useMemo(() => {
    if (!country) return [];
    
    const selectedCountry = locationData.find(c => c.code === country);
    if (!selectedCountry) return [];
  
    const states = (selectedCountry.states?.options || []).map(s => ({
      code: prefixes.state + s.code,
      name: s.name,
      type: 'state'
    }));
  
    const cities = (selectedCountry.cities?.options || []).map(c => ({
      code: prefixes.city + c.code,
      name: c.name,
      type: 'city'
    }));
  
    const allLocations = [...states, ...cities];
    return allLocations.filter(location => 
      location.name.toLowerCase().includes(locationSearch.toLowerCase())
    );
  }, [country, locationSearch, locationData, prefixes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const selectedCountryName = locationData.find(c => c.code === country)?.name.toLowerCase();
      const selectedLocation = filteredLocations.find(l => 
        l.code === (state ? prefixes.state + state : prefixes.city + city)
      )?.name.toLowerCase();

      const newPassword = `${password}_country-${country}${
        state ? `_state-${state}` : `_city-${city}`
      }`;

      // Simulated response with both HTTP and SOCKS5 ports
      setTimeout(() => {
        setGeneratedProxy({
          host: 'geo.nexaproxy.com',
          httpPort: '12321',
          socks5Port: '32325',
          username,
          password: newPassword,
        });
        setIsFormCollapsed(true); // Collapse form after generation
        setIsLoading(false);
      }, 1500);
    } catch (error) {
      console.error('Error generating proxy:', error);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }
    return (
    <div className="min-h-screen bg-dark-bg py-20 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto bg-dark-secondary rounded-2xl p-8 border border-accent/20"
      >
        <h1 className="text-3xl font-bold text-text-primary mb-8 text-center">
          Generate Your Proxy
        </h1>

        {/* Collapsible Form */}
        <div className="mb-8">
          <div 
            className="flex items-center cursor-pointer mb-4" 
            onClick={() => setIsFormCollapsed(!isFormCollapsed)}
          >
            <h2 className="text-xl font-semibold text-text-primary">
              Proxy Configuration
            </h2>
            <span className={`ml-2 transition-transform ${isFormCollapsed ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </div>

          {!isFormCollapsed && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-text-secondary mb-2">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    className="w-full p-3 bg-dark-bg border border-gray-700 rounded focus:ring-2 focus:ring-accent transition-all"
                    required
                    readOnly={searchParams.get('username')}
                  />
                </div>

                <div>
                  <label className="block text-text-secondary mb-2">Password</label>
                  <input
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full p-3 bg-dark-bg border border-gray-700 rounded focus:ring-2 focus:ring-accent transition-all"
                    required
                    readOnly={searchParams.get('password')}
                  />
                </div>

                {/* Country Selection */}
                <div>
                  <label className="block mb-2 text-text-secondary">Select Country</label>
                  {!country ? (
                    <div className="bg-dark-bg border border-gray-700 rounded">
                      <input
                        type="text"
                        placeholder="Search countries..."
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        className="w-full bg-dark-bg p-3 border-b border-gray-700 focus:ring-2 focus:ring-accent transition-all"
                      />
                      <select 
                        value={country}
                        onChange={(e) => {
                          setCountry(e.target.value);
                          setState('');
                          setCity('');
                        }}
                        className="w-full bg-dark-bg p-3 focus:ring-2 focus:ring-accent transition-all"
                        required
                        size={5}
                      >
                        <option value="">Choose a Country</option>
                        {filteredCountries.map((c) => (
                          <option key={c.code} value={c.code} className="bg-dark-bg">
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <div className="flex-grow p-3 bg-dark-bg border border-gray-700 rounded">
                        {locationData.find(c => c.code === country)?.name}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setCountry('');
                          setState('');
                          setCity('');
                          setLocationSearch('');
                        }}
                        className="ml-2 px-4 py-3 bg-dark-bg border border-gray-700 rounded hover:bg-gray-800"
                      >
                        Change
                      </button>
                    </div>
                  )}
                </div>

                {/* Location Selection */}
                {country && (
                  <div>
                    <label className="block mb-2 text-text-secondary">Select Location</label>
                    {!state && !city ? (
                      <div className="bg-dark-bg border border-gray-700 rounded">
                        <input
                          type="text"
                          placeholder="Search locations..."
                          value={locationSearch}
                          onChange={(e) => setLocationSearch(e.target.value)}
                          className="w-full bg-dark-bg p-3 border-b border-gray-700 focus:ring-2 focus:ring-accent transition-all"
                        />
                        <select 
                          value={state || city}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.startsWith(prefixes.state)) {
                              setState(value.replace(prefixes.state, ''));
                              setCity('');
                            } else if (value.startsWith(prefixes.city)) {
                              setCity(value.replace(prefixes.city, ''));
                              setState('');
                            }
                          }}
                          className="w-full bg-dark-bg p-3 focus:ring-2 focus:ring-accent transition-all"
                          required
                          size={8}
                        >
                          <option value="">Choose a Location</option>
                          {filteredLocations.map((location) => (
                            <option 
                              key={location.code} 
                              value={location.code}
                              className="bg-dark-bg"
                            >
                              {location.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <div className="flex-grow p-3 bg-dark-bg border border-gray-700 rounded">
                          {filteredLocations.find(l => 
                            l.code === (state ? prefixes.state + state : prefixes.city + city)
                          )?.name}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setState('');
                            setCity('');
                            setLocationSearch('');
                          }}
                          className="ml-2 px-4 py-3 bg-dark-bg border border-gray-700 rounded hover:bg-gray-800"
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || !country || !(state || city)}
                className="w-full py-3 bg-accent rounded-lg text-white font-semibold hover:bg-accent-hover transition-all duration-300 disabled:opacity-50"
              >
                {isLoading ? 'Generating...' : 'Generate Proxy'}
              </button>
            </form>
          )}
        </div>

{/* Generated Proxy Display */}
{generatedProxy && (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-8 space-y-6"
  >
       {/* Proxy Details */}
       <div className="p-6 bg-dark-bg rounded-lg border border-accent/20">
      <h2 className="text-xl font-semibold text-text-primary mb-4">
        Proxy Details
      </h2>
      <div className="">
        <div className="space-y-2">
          <p className="text-text-secondary">
            <span className="font-medium text-accent">Host:</span>{' '}
            <span className="font-mono">{generatedProxy.host}</span>
          </p>
          <p className="text-text-secondary">
            <span className="font-medium text-accent">HTTP Port:</span>{' '}
            <span className="font-mono">{generatedProxy.httpPort}</span>
          </p>
          <p className="text-text-secondary">
            <span className="font-medium text-accent">SOCKS5 Port:</span>{' '}
            <span className="font-mono">{generatedProxy.socks5Port}</span>
          </p>
        </div>
        <div className="space-y-2">
          <p className="text-text-secondary">
            <span className="font-medium text-accent">Username:</span>{' '}
            <span className="font-mono">{generatedProxy.username}</span>
          </p>
          <p className="text-text-secondary">
            <span className="font-medium text-accent">Password:</span>{' '}
            <span className="font-mono break-all">{generatedProxy.password}</span>
          </p>
        </div>
      </div>
    </div>
    {/* HTTP Proxy */}
    <div className="p-6 bg-dark-bg rounded-lg border border-accent/20">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-text-primary">
          HTTP Proxy
        </h2>
        <span className="px-3 py-1 bg-accent/10 text-accent text-sm rounded-full">
          Port {generatedProxy.httpPort}
        </span>
      </div>
      
      <div className="p-3 bg-dark-secondary rounded border border-gray-700 font-mono text-sm">
        <code className="text-text-secondary break-all">
          {`${generatedProxy.host}:${generatedProxy.httpPort}:${generatedProxy.username}:${generatedProxy.password}`}
        </code>
      </div>

      <button
        onClick={() => {
          const httpProxy = `${generatedProxy.host}:${generatedProxy.httpPort}:${generatedProxy.username}:${generatedProxy.password}`;
          navigator.clipboard.writeText(httpProxy);
          setCopiedProxy(prev => ({ ...prev, http: httpProxy }));
        }}
        className="mt-4 w-full py-2 px-4 bg-accent text-white rounded-lg hover:bg-accent-hover transition-all duration-300 flex items-center justify-center space-x-2"
      >
        <svg 
          className="w-4 h-4" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" 
          />
        </svg>
        <span>Copy HTTP Proxy</span>
      </button>

      {copiedProxy.http && (
        <div className="mt-2 text-center text-sm text-green-500">
          ✓ Copied to clipboard
        </div>
      )}
    </div>

    {/* SOCKS5 Proxy */}
    <div className="p-6 bg-dark-bg rounded-lg border border-accent/20">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-text-primary">
          SOCKS5 Proxy
        </h2>
        <span className="px-3 py-1 bg-accent/10 text-accent text-sm rounded-full">
          Port {generatedProxy.socks5Port}
        </span>
      </div>
      
      <div className="p-3 bg-dark-secondary rounded border border-gray-700 font-mono text-sm">
        <code className="text-text-secondary break-all">
          {`${generatedProxy.host}:${generatedProxy.socks5Port}:${generatedProxy.username}:${generatedProxy.password}`}
        </code>
      </div>

      <button
        onClick={() => {
          const socks5Proxy = `${generatedProxy.host}:${generatedProxy.socks5Port}:${generatedProxy.username}:${generatedProxy.password}`;
          navigator.clipboard.writeText(socks5Proxy);
          setCopiedProxy(prev => ({ ...prev, socks5: socks5Proxy }));
        }}
        className="mt-4 w-full py-2 px-4 bg-accent text-white rounded-lg hover:bg-accent-hover transition-all duration-300 flex items-center justify-center space-x-2"
      >
        <svg 
          className="w-4 h-4" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" 
          />
        </svg>
        <span>Copy SOCKS5 Proxy</span>
      </button>

      {copiedProxy.socks5 && (
        <div className="mt-2 text-center text-sm text-green-500">
          ✓ Copied to clipboard
        </div>
      )}
    </div>
  </motion.div>
)}
      </motion.div>
    </div>
  );
};

export default ProxyGenerator;